<script setup lang="ts">
import { FALLBACK_DOWNLOAD_LINKS, getAppDownloadLinks, normalizeDownloadLinks } from '@/api/download'
import {
  getSurveyPage,
  type SurveyEvaluateItem,
  type SurveyListItem,
  type SurveyPageResponse,
  type SurveyRecentTrader,
} from '@/api/survey'

type SurveyListPageAsyncData = {
  downloadLinks: ReturnType<typeof normalizeDownloadLinks>
  surveyPageData: SurveyPageResponse['data'] | null
}

const DEFAULT_SURVEY_CODE = '8000'
const DEFAULT_PAGE_SIZE = 20
const DEFAULT_API_VERSION = 128
const DEFAULT_RECENT_TRADER_COUNT = 5
/** route → code 静态映射，在 tabList 尚未加载时也能正确解析 URL 中的路由段。 */
const SURVEY_ROUTE_CODE_MAP: Record<string, string> = {
  all: '8000',
  great: '8002',
  good: '8003',
  average: '8006',
  poor: '8004',
  vr: '8005',
  expo: '8007',
}

/** 组件入参，仅暴露当前页码，避免内容组件感知 `/page/1` 假404策略。 */
const props = defineProps<{
  currentPage: number
}>()

const { t, locale } = useAppI18n()
const route = useRoute()

/** 将页码 props 显式收敛成 computed，确保分页切换时依赖统一更新。 */
const currentPage = computed(() => props.currentPage)

const breadcrumbItems = computed(() => [
  {
    label: t('82520'),
    route: '/',
  },
  {
    label: t('82521'),
  },
])

/** 从路由段读取当前选中的评价类型。 */
const getRouteType = () => {
  const category = route.params.category
  if (Array.isArray(category)) {
    return category[0] || ''
  }

  return typeof category === 'string' ? category : ''
}

/** 当前选中的实勘类型路由段完全由路由驱动。 */
const activeTypeRoute = computed(() => getRouteType())

/** 当前筛选项对应的接口 code，未知路由统一回退全部。 */
const activeTypeCode = computed(() => {
  if (!activeTypeRoute.value || activeTypeRoute.value === 'all') {
    return DEFAULT_SURVEY_CODE
  }

  return SURVEY_ROUTE_CODE_MAP[activeTypeRoute.value]
})

/** 统一按当前路由并发加载页面聚合数据与下载地址，首屏 SSR、站内导航 CSR 复用同一入口。 */
const loadSurveyPageData = async (): Promise<SurveyListPageAsyncData> => {
  const [surveyPageResponse, downloadLinksResponse] = await Promise.all([
    getSurveyPage({
      code: activeTypeCode.value || '',
      index: currentPage.value,
      size: DEFAULT_PAGE_SIZE,
      ver: DEFAULT_API_VERSION,
      count: DEFAULT_RECENT_TRADER_COUNT,
    }),
    getAppDownloadLinks().catch(() => null),
  ])

  return {
    downloadLinks: normalizeDownloadLinks(downloadLinksResponse),
    surveyPageData: surveyPageResponse?.data ?? null,
  }
}

/** 获取页面聚合数据，并在分类或页码变化时自动重新请求。 */
const { data: surveyPageData, pending } = await useAsyncData(
  () => `survey-page:${activeTypeCode.value}:${currentPage.value}`,
  loadSurveyPageData,
  {
    default: () => ({
      downloadLinks: FALLBACK_DOWNLOAD_LINKS,
      surveyPageData: null,
    }),
    lazy: true,
    watch: [activeTypeCode, currentPage],
  },
)

/** 按语言跨路由缓存最后一次有效 tabs，页面组件重建时仍保持导航稳定显示。 */
const cachedTabList = useState<SurveyEvaluateItem[]>(`survey-tab-list:${locale.value}`, () => [])

/** 当前接口响应中的 tabs，用于判断请求结果是否有效。 */
const responseTabList = computed<SurveyEvaluateItem[]>(() => surveyPageData.value?.surveyPageData?.tabList ?? [])

/** 展示 tabs 优先使用最新响应，加载间隙回退到上一次有效数据。 */
const tabList = computed<SurveyEvaluateItem[]>(() =>
  responseTabList.value.length > 0 ? responseTabList.value : cachedTabList.value,
)

watch(
  responseTabList,
  (nextTabList) => {
    if (nextTabList.length > 0) {
      cachedTabList.value = nextTabList
    }
  },
  { immediate: true },
)

/** 请求结束后仍缺少有效分类时展示 404，加载期间优先保留骨架屏。 */
const is404 = computed(() => !pending.value && (!activeTypeCode.value || responseTabList.value.length === 0))

const replaceItem = computed(() => {
  const item = tabList.value.find((tab) => tab.code === activeTypeCode.value)
  return item?.name || ''
})
const seoTitle = computed(() => t('84371', [replaceItem.value]))
const seoDescription = computed(() => t('84368', [replaceItem.value]))
useHead(() => ({
  title: seoTitle.value,
  meta: [
    {
      name: 'description',
      content: seoDescription.value,
    },
  ],
}))
/** 列表总数由当前聚合响应直接派生。 */
const surveyListTotal = computed<number>(() => Number(surveyPageData.value?.surveyPageData?.list?.total ?? 0))

/** 列表数据由当前聚合响应直接派生。 */
const surveyListItems = computed<SurveyListItem[]>(() => surveyPageData.value?.surveyPageData?.list?.items ?? [])

/** 右侧最近实勘交易商列表。 */
const recentTraders = computed<SurveyRecentTrader[]>(() => surveyPageData.value?.surveyPageData?.recentTrader ?? [])

/** 右侧下载卡片使用的 App 下载地址。 */
const downloadLinks = computed(() => surveyPageData.value?.downloadLinks ?? FALLBACK_DOWNLOAD_LINKS)

/** 当前接口分页大小固定为 20。 */
const pageSize = computed(() => DEFAULT_PAGE_SIZE)

/** 分页组件内部使用的路由模板，只传分类段与页码占位符。 */
const pageRoute = computed(() => {
  const currentCategory = activeTypeRoute.value || 'all'
  return `/inspections/${currentCategory}/page/{idx}`
})

/** 页脚高度，SSR 用默认值兜底，客户端挂载后动态测量。 */
const footerHeight = ref(315)
onMounted(() => {
  // 动态获取页脚 DOM 高度，使列表区最小高度精确填满视口剩余空间
  const footerEl = document.querySelector('.wiki-bit-pc-bootom-map') as HTMLElement | null
  if (footerEl) {
    footerHeight.value = footerEl.offsetHeight
  } else {
    footerHeight.value = 0
  }
})
</script>

<template>
  <CommonNotFound v-if="is404" />
  <div
    v-else
    class="survey-list-container mx-auto w-[clamp(800px,calc(100%-64px),1200px)] pt-[70px] pb-[36px]"
    :style="{
      minHeight: `calc(100vh - ${footerHeight}px)`,
    }"
  >
    <CommonBreadcrumb :items="breadcrumbItems" />
    <div class="flex w-full justify-between gap-[20px] max-[1000px]:flex-col">
      <div class="w-full max-w-[752px] min-w-0 flex-1 max-[1000px]:max-w-full">
        <div class="sticky top-[70px] z-[10] bg-white pb-[8px]">
          <SurveyListType :list="tabList" :active-route="activeTypeRoute" />
        </div>
        <div v-if="pending" role="status" aria-live="polite" aria-label="Loading survey list" aria-busy="true">
          <SurveyListItemSkeleton v-for="item in 6" :key="`survey-list-skeleton-${item}`" />
        </div>
        <div v-else-if="surveyListItems.length > 0">
          <div v-for="(item, index) in surveyListItems" :key="item.sid || item.code">
            <SurveyListItem :item="item" :index="index" />
          </div>
        </div>
        <CommonEmptyState v-else />

        <div v-if="!pending" class="mt-[32px] flex justify-center">
          <CommonPagination
            :current-page="currentPage"
            :total="surveyListTotal"
            :page-size="pageSize"
            :page-route="pageRoute"
          />
        </div>
      </div>

      <div class="flex w-[384px] min-w-0 shrink-0 flex-col gap-[20px] max-[1000px]:w-full max-[1000px]:flex-row">
        <SurveyListTraders v-if="pending || recentTraders.length > 0" :loading="pending" :survey-list="recentTraders" />
        <div class="sticky top-[90px] max-[1000px]:hidden">
          <CommonDownload :download-links="downloadLinks" />
        </div>
      </div>
    </div>
  </div>
</template>
